import React, { createContext, useContext, useState, useEffect } from 'react';
import { ROUTES } from '../config/routes';
import { setJSONCookie, getJSONCookie, removeCookie } from '../utils/cookieUtils';

/**
 * 登録フローコンテキスト
 * クッキーを使用してステップ管理を行う
 */
const RegistrationFlowContext = createContext();

export const useRegistrationFlow = () => {
  const context = useContext(RegistrationFlowContext);
  if (!context) {
    throw new Error('useRegistrationFlow must be used within RegistrationFlowProvider');
  }
  return context;
};

/**
 * 登録ステップの定義
 */
export const REGISTRATION_STEPS = {
  TERMS_AGREEMENT: 'TERMS_AGREEMENT',
  EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
  MEMBER_REGISTRATION: 'MEMBER_REGISTRATION',
  PAYMENT_SETUP: 'PAYMENT_SETUP',
  REGISTRATION_COMPLETE: 'REGISTRATION_COMPLETE'
};

/**
 * ステップとルートのマッピング
 */
export const STEP_ROUTES = {
  [REGISTRATION_STEPS.TERMS_AGREEMENT]: ROUTES.TERMS,
  [REGISTRATION_STEPS.EMAIL_VERIFICATION]: ROUTES.AUTH_CODE,
  [REGISTRATION_STEPS.MEMBER_REGISTRATION]: ROUTES.REGISTER,
  [REGISTRATION_STEPS.PAYMENT_SETUP]: ROUTES.PAYMENT,
  [REGISTRATION_STEPS.REGISTRATION_COMPLETE]: ROUTES.COMPLETE
};

// クッキーのキー名
const COOKIE_KEYS = {
  CURRENT_STEP: 'registration_current_step',
  COMPLETED_STEPS: 'registration_completed_steps',
  FLOW_COMPLETED: 'registration_flow_completed'
};

/**
 * 登録フロープロバイダー
 */
const RegistrationFlowProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(REGISTRATION_STEPS.TERMS_AGREEMENT);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isFlowCompleted, setIsFlowCompleted] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    termsAgreed: false,
    email: '',
    emailVerified: false,
    paymentMethod: null
  });

  /**
   * クッキーからステップ情報を復元
   */
  useEffect(() => {
    try {
      const savedStep = getJSONCookie(COOKIE_KEYS.CURRENT_STEP);
      const savedCompletedSteps = getJSONCookie(COOKIE_KEYS.COMPLETED_STEPS);
      const savedFlowCompleted = getJSONCookie(COOKIE_KEYS.FLOW_COMPLETED);

      if (savedStep && Object.values(REGISTRATION_STEPS).includes(savedStep)) {
        setCurrentStep(savedStep);
      }

      if (savedCompletedSteps && Array.isArray(savedCompletedSteps)) {
        setCompletedSteps(new Set(savedCompletedSteps));
      }

      if (savedFlowCompleted === true) {
        setIsFlowCompleted(true);
      }

    } catch (error) {
      console.error('[REGISTRATION FLOW] Failed to restore step information from cookies:', error);
    }
  }, []);

  /**
   * クッキーにステップ情報を保存
   */
  const saveStepInfoToCookie = (step, completed, flowCompleted = false) => {
    try {
      // 7日間有効なクッキーとして保存
      setJSONCookie(COOKIE_KEYS.CURRENT_STEP, step, 7);
      setJSONCookie(COOKIE_KEYS.COMPLETED_STEPS, [...completed], 7);
      setJSONCookie(COOKIE_KEYS.FLOW_COMPLETED, flowCompleted, 7);
  
    } catch (error) {
      console.error('[REGISTRATION FLOW] Failed to save step information to cookies:', error);
    }
  };

  /**
   * 次のステップを決定
   */
  const getNextStep = (currentStep, data) => {
    switch (currentStep) {
      case REGISTRATION_STEPS.TERMS_AGREEMENT:
        return REGISTRATION_STEPS.EMAIL_VERIFICATION;
      
      case REGISTRATION_STEPS.EMAIL_VERIFICATION:
        return REGISTRATION_STEPS.MEMBER_REGISTRATION;
      
      case REGISTRATION_STEPS.MEMBER_REGISTRATION:
        return REGISTRATION_STEPS.PAYMENT_SETUP;
      
      case REGISTRATION_STEPS.PAYMENT_SETUP:
        return REGISTRATION_STEPS.REGISTRATION_COMPLETE;
      
      default:
        return REGISTRATION_STEPS.REGISTRATION_COMPLETE;
    }
  };

  /**
   * ステップを完了として記録し、次のステップに進む
   */
  const completeStep = (step, stepData = {}) => {
    
    const newCompletedSteps = new Set([...completedSteps, step]);

    // センシティブな情報は一切状態に保存しない
    const filteredStepData = {};
    
    // 保存を許可する項目のみフィルタリング
    if (stepData.termsAgreed !== undefined) {
      filteredStepData.termsAgreed = stepData.termsAgreed;
    }
    if (stepData.email !== undefined) {
      filteredStepData.email = stepData.email;
    }
    if (stepData.emailVerified !== undefined) {
      filteredStepData.emailVerified = stepData.emailVerified;
    }
    if (stepData.paymentMethod !== undefined) {
      filteredStepData.paymentMethod = stepData.paymentMethod;
    }

    const newData = { ...registrationData, ...filteredStepData };
    
    // 次のステップを決定
    const nextStep = getNextStep(step, newData);
    
    setCompletedSteps(newCompletedSteps);
    setRegistrationData(newData);
    setCurrentStep(nextStep);

    // 登録完了の場合はフラグを設定
    const flowCompleted = nextStep === REGISTRATION_STEPS.REGISTRATION_COMPLETE;
    if (flowCompleted) {
      setIsFlowCompleted(true);
    }

    // クッキーにステップ情報を保存
    saveStepInfoToCookie(nextStep, newCompletedSteps, flowCompleted);

  };

  /**
   * 登録データを更新
   */
  const updateRegistrationData = (data) => {
    setRegistrationData(prev => ({ ...prev, ...data }));
  };

  /**
   * 現在のステップを更新
   */
  const updateCurrentStep = (step) => {
    if (Object.values(REGISTRATION_STEPS).includes(step)) {
      setCurrentStep(step);
      saveStepInfoToCookie(step, completedSteps, isFlowCompleted);
    }
  };

  /**
   * 指定されたステップにアクセス可能かチェック
   */
  const canAccessStep = (step) => {
    if (isFlowCompleted) {
      return step === REGISTRATION_STEPS.REGISTRATION_COMPLETE;
    }

    switch (step) {
      case REGISTRATION_STEPS.TERMS_AGREEMENT:
        return true;
      
      case REGISTRATION_STEPS.EMAIL_VERIFICATION:
        return completedSteps.has(REGISTRATION_STEPS.TERMS_AGREEMENT);
      
      case REGISTRATION_STEPS.MEMBER_REGISTRATION:
        return completedSteps.has(REGISTRATION_STEPS.EMAIL_VERIFICATION);
      
      case REGISTRATION_STEPS.PAYMENT_SETUP:
        return completedSteps.has(REGISTRATION_STEPS.MEMBER_REGISTRATION);
      
      case REGISTRATION_STEPS.REGISTRATION_COMPLETE:
        return isFlowCompleted || completedSteps.has(REGISTRATION_STEPS.PAYMENT_SETUP);
      
      default:
        return false;
    }
  };

  /**
 * ステップを完了として記録するが、次のステップには進まない
 * （口座振替で登録完了画面に直接行く場合など）
 */
const completeStepWithoutAdvance = (step, stepData = {}) => {
  
  const newCompletedSteps = new Set([...completedSteps, step]);

  // センシティブな情報は一切状態に保存しない
  const filteredStepData = {};
  
  if (stepData.termsAgreed !== undefined) {
    filteredStepData.termsAgreed = stepData.termsAgreed;
  }
  if (stepData.email !== undefined) {
    filteredStepData.email = stepData.email;
  }
  if (stepData.emailVerified !== undefined) {
    filteredStepData.emailVerified = stepData.emailVerified;
  }
  if (stepData.paymentMethod !== undefined) {
    filteredStepData.paymentMethod = stepData.paymentMethod;
  }

  const newData = { ...registrationData, ...filteredStepData };
  
  setCompletedSteps(newCompletedSteps);
  setRegistrationData(newData);
  // currentStepは更新しない

  // クッキーにステップ情報を保存
  saveStepInfoToCookie(currentStep, newCompletedSteps, isFlowCompleted);

};

  /**
   * 指定されたルートにアクセス可能かチェック
   */
  const canAccessRoute = (route) => {
    const step = Object.keys(STEP_ROUTES).find(key => STEP_ROUTES[key] === route);
    if (!step) {
      return true;
    }
    return canAccessStep(step);
  };

  /**
   * 登録フローをリセット
   */
  const resetFlow = () => {
    setCurrentStep(REGISTRATION_STEPS.TERMS_AGREEMENT);
    setCompletedSteps(new Set());
    setIsFlowCompleted(false);
    setRegistrationData({
      termsAgreed: false,
      email: '',
      emailVerified: false,
      paymentMethod: null
    });
    
    // クッキーもクリア
    Object.values(COOKIE_KEYS).forEach(key => {
      removeCookie(key);
    });
  };

  /**
   * 現在のステップのルートを取得
   */
  const getCurrentStepRoute = () => {
    return STEP_ROUTES[currentStep] || '/terms';
  };

  /**
   * 登録完了時の処理
   */
  const completeRegistration = () => {
    setIsFlowCompleted(true);
    
    // クッキーにも完了状態を保存
    saveStepInfoToCookie(currentStep, completedSteps, true);
    
    // 登録完了後、残りのデータもクリア（念のため）
    setTimeout(() => {
      setRegistrationData({
        termsAgreed: false,
        email: '',
        emailVerified: false,
        paymentMethod: null
      });
    }, 5000);
  };

  /**
   * セッション終了時の処理
   */
  const clearSession = () => {
    resetFlow();
  };

  /**
   * 登録フロー中かどうかを判定
   */
  const isInRegistrationFlow = () => {
    if (isFlowCompleted) {
      return false;
    }
    
    if (currentStep !== REGISTRATION_STEPS.TERMS_AGREEMENT || completedSteps.size > 0) {
      return true;
    }
    
    return false;
  };

  /**
   * ステップデータを取得
   */
  const getStepData = (step) => {
    return registrationData;
  };

  /**
   * 検証済みメールアドレスを取得
   */
  const getVerifiedEmail = () => {
    return registrationData.emailVerified ? registrationData.email : null;
  };

  /**
   * すべての登録データを取得
   */
  const getAllRegistrationData = () => {
    return { ...registrationData };
  };

  /**
   * クッキー情報の取得（デバッグ用）
   */
  const getCookieInfo = () => {
    const info = {};
    Object.entries(COOKIE_KEYS).forEach(([key, cookieKey]) => {
      try {
        const value = getJSONCookie(cookieKey);
        info[key] = {
          exists: value !== null,
          value: value
        };
      } catch (error) {
        info[key] = { error: error.message };
      }
    });
    return info;
  };

  /**
   * デバッグ情報の取得
   */
  const getDebugInfo = () => {
    return {
      currentStep,
      completedSteps: [...completedSteps],
      registrationData: {
        termsAgreed: registrationData.termsAgreed,
        emailVerified: registrationData.emailVerified,
        paymentMethod: registrationData.paymentMethod
      },
      isFlowCompleted,
      isInRegistrationFlow: isInRegistrationFlow(),
      cookieInfo: getCookieInfo(),
      securityNote: 'No sensitive data stored in memory or cookies',
      canAccessSteps: Object.values(REGISTRATION_STEPS).map(step => ({
        step,
        canAccess: canAccessStep(step)
      }))
    };
  };

  const value = {
    // 状態
    currentStep,
    completedSteps,
    registrationData,
    isFlowCompleted,

    // アクション
    completeStep,
    completeStepWithoutAdvance,
    updateRegistrationData,
    updateCurrentStep,
    canAccessStep,
    canAccessRoute,
    resetFlow,
    getCurrentStepRoute,
    completeRegistration,
    isInRegistrationFlow,
    clearSession,

    // データ取得関数
    getStepData,
    getVerifiedEmail,
    getAllRegistrationData,

    // ユーティリティ
    getDebugInfo,
    getCookieInfo,
    
    // 定数
    REGISTRATION_STEPS,
    STEP_ROUTES
  };

  return (
    <RegistrationFlowContext.Provider value={value}>
      {children}
    </RegistrationFlowContext.Provider>
  );
};

export { RegistrationFlowProvider };
export default RegistrationFlowProvider;